import { roundPercent } from "@/lib/partners/percent"
import { createTranslator, type useTranslations } from "next-intl"
import english from "@/translations/en/partner-bonus.json"

export type PartnerBonusTranslator = ReturnType<typeof useTranslations<"PartnerBonus">>
const englishTranslator = createTranslator({locale: "en", messages: english, namespace: "PartnerBonus"})


/** Shared by the cabinet and the admin console: both edit the same bonus fields. */

export type BonusLimitMode = "always" | "once" | "limited"

export function getBONUS_LIMIT_OPTIONS(t: ReturnType<typeof useTranslations<"Metadata.PartnersClientBonus">>): readonly { value: BonusLimitMode; label: string }[] {
  return [
  { value: "always", label: t("everyPayment") },
  { value: "once", label: t("firstPaymentOnly") },
  { value: "limited", label: t("firstNPayments") },
]
}

const MIN_LIMITED_PAYMENTS = 2
const MAX_LIMITED_PAYMENTS = 1000

export function bonusLimitModeFor(limit: number | null): BonusLimitMode {
  switch (limit) {
    case null:
      return "always"
    case 1:
      return "once"
    default:
      return "limited"
  }
}

/** NULL means every payment, which is why "always" is not 0. */
export function bonusLimitFrom(mode: BonusLimitMode, count: string): number | null {
  switch (mode) {
    case "always":
      return null
    case "once":
      return 1
    case "limited":
      return Number(count)
  }
}

export function bonusLimitCountError(count: string, t: PartnerBonusTranslator = englishTranslator): string | null {
  const parsed = Number(count)
  return Number.isInteger(parsed) && parsed >= MIN_LIMITED_PAYMENTS && parsed <= MAX_LIMITED_PAYMENTS
    ? null
    : t("limitCount", {min: MIN_LIMITED_PAYMENTS, max: MAX_LIMITED_PAYMENTS})
}

/** `ceilingMessage` differs per surface: the cabinet talks about the partner's percent, the console about the link's. */
export function bonusPercentError(raw: string, ceilingPercent: number, ceilingMessage: string, t: PartnerBonusTranslator = englishTranslator): string | null {
  const trimmed = raw.trim()
  const parsed = Number(trimmed.replace(",", "."))

  if (!trimmed || !Number.isFinite(parsed) || parsed < 0) return t("percentage")
  if (parsed > ceilingPercent) return ceilingMessage
  if (roundPercent(parsed) !== parsed) return t("decimalPlaces")
  return null
}

export function bonusScheduleLabel(limit: number | null, t: PartnerBonusTranslator = englishTranslator): string {
  switch (limit) {
    case null:
      return t("everyPayment")
    case 1:
      return t("firstPayment")
    default:
      return t("firstPayments", {count: limit})
  }
}

/** Empty means "follow the inherited percent", so it maps to NULL rather than 0. */
export function parsePercentInput(raw: string): number | null | "invalid" {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const parsed = Number(trimmed.replace(",", "."))
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return "invalid"
  return roundPercent(parsed)
}

export function parseBonusPercent(raw: string): number {
  return Number(raw.replace(",", ".")) || 0
}
